import RecoverableImage from '../images/RecoverableImage';
import { profileWords } from '../profile/profileConnectionModel';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiList, FiMessageSquare } from 'react-icons/fi';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import baseline from '../../app-ui/baseline';
import ContentTitle from '../../components/content/ContentTitle';
import HydratedPlaylistArtwork from '../../components/content/HydratedPlaylistArtwork';
import { albumRecordPath, recordPath } from '../../pages/mypage/components/myRecordsModel';
import {
  getTastePersonaPresentation,
  getLocalizedTastePersonaTypeName,
  getLocalizedTastePersonaQuip,
  getLocalizedTasteEvidence,
} from './generated/persona';
import { getLocalizedTasteGenre, getLocalizedTasteDecade } from './generated/localization';
import { completeDecades } from './generated/decades';
import {
  tasteTranslate,
  regionSplit,
  tasteArtistLink,
  ratingProgress,
  progressWindow,
  sampledNumber,
} from './tasteModel';
import useMyTaste from './useMyTaste';
import useTasteMotion from './useTasteMotion';
import './MyTaste.css';

function Image({ url, label = '', className = '' }) {
  return (
    <span className={`pp-taste-image ${className}`} aria-hidden="true">
      {url ? (
        <RecoverableImage
          src={url}
          alt=""
          loading="lazy"
          fallback={<>{label.trim().slice(0, 1).toUpperCase() || 'P'}</>}
        />
      ) : (
        label.trim().slice(0, 1).toUpperCase() || 'P'
      )}
    </span>
  );
}
function Chevron() {
  return <FiChevronRight size={17} aria-hidden="true" />;
}
function NumberText({
  progress,
  value,
  start = 0,
  end = 1,
  from = 0,
  decimals = 0,
  format = String,
  className = '',
}) {
  const final = format(Number(value).toFixed(decimals));
  return (
    <span className={className} aria-label={final}>
      {format(sampledNumber(progress, value, start, end, from).toFixed(decimals))}
    </span>
  );
}
function Reveal({ progress, start, end, children, className = '' }) {
  const local = progressWindow(progress, start, end);
  return (
    <div
      className={className}
      style={{ opacity: local, transform: `translateY(${5 * (1 - local)}px)` }}
    >
      {children}
    </div>
  );
}
function Region({ data, t, detail = false }) {
  const [ref, raw] = useTasteMotion({ duration: 460, deferred: detail, enabled: detail });
  const progress = detail ? 1 - Math.pow(1 - raw, 3) : 1;
  const split = regionSplit(data);
  if (!split.classified) return detail ? <p>{t('taste.regionEmpty')}</p> : null;
  return (
    <div ref={ref} className="pp-taste-region">
      <div className="pp-taste-region-labels">
        <span>
          {t('taste.domestic')}{' '}
          <NumberText
            progress={progress}
            from={50}
            value={split.domestic}
            format={(v) => `${v}%`}
          />
        </span>
        <span>
          {t('taste.international')}{' '}
          <NumberText
            progress={progress}
            from={50}
            value={split.overseas}
            format={(v) => `${v}%`}
          />
        </span>
      </div>
      <div className="pp-taste-region-track" aria-hidden="true">
        <span style={{ width: `${50 + (split.domestic - 50) * progress}%` }} />
      </div>
      {detail && <small>{t('taste.regionBasis', { count: split.classified })}</small>}
    </div>
  );
}
function Unlock({ data, t, detail, canStart = true }) {
  return (
    <div className={`pp-taste-unlock${detail ? ' is-detail' : ''}`}>
      <strong>{t(detail ? 'taste.emptyTitle' : 'taste.unlockPrompt')}</strong>
      <p>{t(detail ? 'taste.emptyBody' : 'taste.unlockProgress', { count: data.ratingCount })}</p>
      <div
        className="pp-taste-progress"
        role="progressbar"
        aria-label={t('taste.currentCount', { count: data.ratingCount })}
        aria-valuemin={0}
        aria-valuemax={5}
        aria-valuenow={Math.max(0, Math.min(5, data.ratingCount))}
      >
        <span style={{ width: `${ratingProgress(data.ratingCount) * 100}%` }} />
      </div>
      {detail ? (
        <small>{t('taste.currentCount', { count: data.ratingCount })}</small>
      ) : canStart ? (
        <Link className="pp-taste-start" to="/search">
          {t(data.ratingCount > 0 ? 'taste.continueRating' : 'taste.startRating')}
          <Chevron />
        </Link>
      ) : null}
    </div>
  );
}
function Summary({ data, features, t, locale, publicProfile, profile }) {
  const persona = getTastePersonaPresentation(data),
    artists = data.topArtists.slice(0, 3),
    genre = data.topGenres[0],
    decade = data.topDecades[0];
  const review = features?.representativeReview,
    playlist = features?.representativePlaylist;
  return (
    <div className="pp-taste-summary-grid">
      <div className="pp-taste-analysis">
        {persona ? (
          <>
            <div className="pp-taste-analysis-top">
              <div className="pp-taste-summary-score">
                <strong>{data.averageRating.toFixed(1)}</strong>
                <small>{t('taste.averageRating')}</small>
              </div>
              <div className="pp-taste-summary-persona">
                <strong>{getLocalizedTastePersonaTypeName(persona, t)}</strong>
                <small>{getLocalizedTastePersonaQuip(persona, locale, t)}</small>
              </div>
            </div>
            <Region data={data} t={t} />
          </>
        ) : (
          <Unlock data={data} t={t} canStart={!publicProfile} />
        )}
      </div>
      {(artists.length > 0 || genre || decade) && (
        <div className="pp-taste-identity">
          <div className="pp-taste-artists">
            {artists.map((artist, i) => (
              <Link
                key={artist.artistSlug ?? `${artist.label}-${i}`}
                {...tasteArtistLink(artist)}
                aria-label={t('taste.openArtist', { artist: artist.label })}
              >
                <Image url={artist.imageUrl} label={artist.label} className="is-round" />
                <span>{artist.label}</span>
              </Link>
            ))}
          </div>
          <div className="pp-taste-tags">
            {genre && (
              <span>
                {t('taste.genreTag', { genre: getLocalizedTasteGenre(genre.label, locale) })}
              </span>
            )}
            {decade && (
              <span>
                {t('taste.eraTag', { era: getLocalizedTasteDecade(decade.label, locale) })}
              </span>
            )}
          </div>
        </div>
      )}
      {review && (
        <Link className="pp-taste-feature" to={recordPath(review, 'reviews')}>
          <Image url={review.album.imageUrl} />
          <span className="pp-taste-copy">
            <strong className="pp-taste-feature-label">
              <FiMessageSquare size={13} />
              {t('taste.featuredReview')}
            </strong>
            <ContentTitle
              title={review.album.title}
              explicit={review.album.contentRating === 'EXPLICIT'}
              inlineExplicit
            />
            <span className="pp-taste-feature-meta">
              <span>{review.artist?.name ?? t('common.artist')}</span>
              <span>
                · <span className="pp-taste-star">★</span> {Number(review.review.rating).toFixed(1)}
              </span>
            </span>
          </span>
          <Chevron />
        </Link>
      )}
      {playlist && (
        <Link className="pp-taste-feature" to={recordPath(playlist, 'lists')}>
          <HydratedPlaylistArtwork playlist={playlist} />
          <span className="pp-taste-copy">
            <strong className="pp-taste-feature-label">
              <FiList size={13} />
              {t('taste.featuredPlaylist')}
            </strong>
            <ContentTitle title={playlist.title} />
            <small>
              {t('taste.collectionCount', {
                count: playlist.type === 'ALBUM' ? playlist.albumCount : playlist.trackCount,
                type: t(playlist.type === 'ALBUM' ? 'activity.albumType' : 'activity.trackType'),
              })}
            </small>
          </span>
          <Chevron />
        </Link>
      )}
      <Link
        className="pp-taste-details"
        to={
          publicProfile ? `/users/${encodeURIComponent(profile.nickname)}/taste` : '/mypage/taste'
        }
      >
        {t('taste.details')}
        <FiChevronRight size={16} />
      </Link>
    </div>
  );
}
function Heading({ title, meta }) {
  return (
    <div className="pp-taste-section-heading">
      <h2>{title}</h2>
      {meta && <small>{meta}</small>}
    </div>
  );
}
function Decades({ data, t, locale }) {
  const [section, raw] = useTasteMotion({ duration: 820, deferred: true });
  const p = 1 - Math.pow(1 - raw, 3),
    rail = useRef(null);
  const items = useMemo(() => completeDecades(data.topDecades), [data.topDecades]);
  const max = Math.max(1, ...data.topDecades.map((x) => x.count));
  const strongest = items.reduce(
    (best, item, index) => (item.count > items[best].count ? index : best),
    0,
  );
  useLayoutEffect(() => {
    const node = rail.current;
    if (!node) return;
    const center = () => {
      const item = node.firstElementChild?.children[strongest];
      if (item)
        node.scrollLeft = Math.max(
          0,
          Math.min(
            node.scrollWidth - node.clientWidth,
            item.offsetLeft + item.offsetWidth / 2 - node.clientWidth / 2,
          ),
        );
    };
    center();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(center);
    observer?.observe(node);
    for (const child of node.firstElementChild?.children ?? []) observer?.observe(child);
    return () => observer?.disconnect();
  }, [strongest, items]);
  return (
    <section className="pp-taste-section pp-taste-eras" ref={section}>
      <Heading title={t('taste.frequentEras')} meta={t('taste.albumCount')} />
      <div
        ref={rail}
        className="pp-taste-decade-viewport"
        tabIndex={0}
        role="region"
        aria-label={t('taste.frequentEras')}
        style={{
          '--line-progress': progressWindow(p, 0, 0.78),
          '--decade-count': items.length,
          '--decade-columns': Math.ceil(items.length / 2),
        }}
      >
        <div className="pp-taste-decades">
          {items.map((item, index) => {
            const start = 0.1 + Math.min(index, 7) * 0.07,
              end = Math.min(0.84, start + 0.22),
              local = progressWindow(p, start, end),
              emphasis = progressWindow(p, 0.78, 1),
              scale = item.count === max ? 1 + 0.16 * (1 - Math.abs(2 * emphasis - 1)) : 1;
            return (
              <div
                className={`pp-taste-decade${item.count === max ? ' is-strong' : ''}`}
                key={item.label}
              >
                <i
                  aria-hidden="true"
                  style={{ opacity: local, transform: `scale(${(0.65 + 0.35 * local) * scale})` }}
                />
                <span>{getLocalizedTasteDecade(item.label, locale)}</span>
                <NumberText
                  progress={p}
                  value={item.count}
                  start={start}
                  end={end}
                  format={(v) => t('taste.albumCountValue', { count: v })}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
function Detail({ data, profile, t, locale }) {
  const [, progress] = useTasteMotion();
  const persona = getTastePersonaPresentation(data);
  const evidence = getLocalizedTasteEvidence(persona, data, locale, t);
  const max = Math.max(1, ...data.topGenres.map((x) => x.count));
  return (
    <div className="pp-taste-detail-grid">
      <div className="pp-taste-detail-hero">
        <NumberText
          progress={progress}
          value={data.ratingCount}
          end={0.18}
          className="pp-taste-eyebrow"
          format={(v) => t('taste.analyzedCount', { count: v })}
        />
        <Reveal progress={progress} start={0.08} end={0.25} className="pp-taste-persona">
          <Image url={profile.profileImageUrl} label={profile.nickname} className="is-round" />
          <div>
            <small>{t('taste.representative')}</small>
            <h2>{getLocalizedTastePersonaTypeName(persona, t)}</h2>
            <p>{getLocalizedTastePersonaQuip(persona, locale, t)}</p>
          </div>
        </Reveal>
        <div className="pp-taste-reasons">
          <strong>{t('taste.judgmentBasis')}</strong>
          {evidence.map((reason, index) => (
            <Reveal
              key={index}
              progress={progress}
              start={0.18 + index * 0.07}
              end={0.33 + index * 0.07}
              className="pp-taste-reason"
            >
              <small>{String(index + 1).padStart(2, '0')}</small>
              <p>{reason}</p>
            </Reveal>
          ))}
        </div>
      </div>
      <div className="pp-taste-stats">
        <div>
          <small>{t('taste.averageRating')}</small>
          <NumberText
            className="is-average"
            progress={progress}
            value={data.averageRating}
            start={0.16}
            end={0.4}
            decimals={1}
          />
        </div>
        <div>
          <small>{t('taste.mostFrequentRating')}</small>
          {data.dominantRating == null ? (
            <strong>—</strong>
          ) : (
            <NumberText
              progress={progress}
              value={data.dominantRating}
              start={0.2}
              end={0.44}
              decimals={1}
            />
          )}
        </div>
        <div>
          <small>{t('taste.fullReviews')}</small>
          <NumberText
            progress={progress}
            value={data.reviewCount}
            start={0.24}
            end={0.48}
            format={(v) => t('taste.reviewCount', { count: v })}
          />
        </div>
      </div>
      {data.topGenres.length > 0 && (
        <section className="pp-taste-section is-first pp-taste-genres">
          <Heading title={t('taste.center')} meta={t('taste.albumAppearances')} />
          {data.topGenres.slice(0, 5).map((item, index) => {
            const start = 0.32 + index * 0.045,
              end = Math.min(0.72, start + 0.25);
            return (
              <div key={item.label} className="pp-taste-genre">
                <span title={getLocalizedTasteGenre(item.label, locale)}>
                  {getLocalizedTasteGenre(item.label, locale)}
                </span>
                <div className="pp-taste-genre-track" aria-hidden="true">
                  <i
                    style={{
                      width: `${(item.count / max) * 100}%`,
                      opacity: Math.max(0.48, 1 - index * 0.12),
                      transform: `scaleX(${progressWindow(progress, start, end)})`,
                    }}
                  />
                </div>
                <span className="pp-taste-genre-value">
                  <NumberText value={item.count} progress={progress} start={start} end={end} />
                  <small>{item.averageRating.toFixed(1)}</small>
                </span>
              </div>
            );
          })}
        </section>
      )}
      {data.topDecades.length > 0 && <Decades data={data} t={t} locale={locale} />}
      <section className="pp-taste-section pp-taste-regions">
        <Heading title={t('taste.musicRegion')} meta={t('taste.ratedAlbumsBasis')} />
        <Region data={data} t={t} detail />
      </section>
      {data.topArtists.length > 0 && (
        <section className="pp-taste-section pp-taste-top-artists">
          <Heading title={t('taste.frequentArtists')} meta={t('taste.ratedAlbums')} />
          <div>
            {data.topArtists.slice(0, 5).map((artist, index) => (
              <Link
                className="pp-taste-artist-row"
                key={`${artist.label}-${index}`}
                {...tasteArtistLink(artist)}
              >
                <small className="pp-taste-rank">{String(index + 1).padStart(2, '0')}</small>
                <Image url={artist.imageUrl} label={artist.label} className="is-round" />
                <span className="pp-taste-copy">
                  <span className="pp-taste-artist-name">{artist.label}</span>
                  <small>
                    {t('taste.artistStats', {
                      count: artist.count,
                      average: artist.averageRating.toFixed(1),
                    })}
                  </small>
                </span>
                <Chevron />
              </Link>
            ))}
          </div>
        </section>
      )}
      {data.mostDivergentAlbums.length > 0 && (
        <section
          className={`pp-taste-section pp-taste-divergent${data.topArtists.length ? ' follows-artists' : ''}`}
        >
          <Heading title={t('taste.differentAlbums')} meta={t('taste.communityComparison')} />
          <div>
            {data.mostDivergentAlbums.map((item) => (
              <Link
                key={item.albumId}
                className="pp-taste-album-row"
                to={albumRecordPath({ id: item.albumId, slug: item.albumSlug })}
              >
                <Image url={item.imageUrl} />
                <span className="pp-taste-copy">
                  <ContentTitle title={item.albumTitle} />
                  <small>
                    {t('taste.scoreComparison', {
                      mine: item.myRating.toFixed(1),
                      community: item.communityRating.toFixed(1),
                    })}
                  </small>
                </span>
                <Chevron />
              </Link>
            ))}
          </div>
        </section>
      )}
      {Object.values(data.ratingDistribution).reduce((sum, x) => sum + x, 0) > 0 && (
        <small className="pp-taste-footnote">{t('taste.analysisFootnote')}</small>
      )}
    </div>
  );
}
export default function MyTaste({ profile, detail = false, publicProfile = false }) {
  const { locale } = useWebLocale(),
    { resolvedMode } = useColorScheme(),
    navigate = useNavigate();
  const t = tasteTranslate(locale),
    colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const { enabled, taste, features } = useMyTaste(profile, !detail, publicProfile);
  const [expanded, setExpanded] = useState(false),
    probe = useRef(null);
  useLayoutEffect(() => {
    const measure = () => {
      if (probe.current) setExpanded(probe.current.getBoundingClientRect().height >= 22.4);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (probe.current) observer?.observe(probe.current);
    return () => observer?.disconnect();
  }, [enabled]);
  if (!enabled) return null;
  const publicTitle = profileWords(locale)('profile.memberTaste', { nickname: profile.nickname });
  const loading = taste.isPending || (!detail && features.isPending);
  const errors = [taste, ...(!detail ? [features] : [])].filter((q) => q.isError);
  return (
    <section
      className={`pp-taste ${detail ? 'is-detail' : 'is-summary'}${expanded ? ' is-expanded' : ''}`}
      aria-label={
        detail ? t('taste.analysisTitle') : publicProfile ? publicTitle : t('my.musicTaste')
      }
      style={{
        '--taste-text': colors.text,
        '--taste-secondary': colors.textSecondary,
        '--taste-muted': colors.textMuted,
        '--taste-border': colors.divider,
        '--taste-border-strong': colors.dividerStrong,
        '--taste-bg': colors.background,
        '--taste-surface': colors.surfaceMuted,
      }}
    >
      <span ref={probe} className="pp-taste-font-probe" aria-hidden="true" />
      {detail ? (
        <header className="pp-taste-header">
          <button
            type="button"
            aria-label={t('common.back')}
            onClick={() =>
              window.history.state?.idx > 0
                ? navigate(-1)
                : navigate(
                    publicProfile ? `/users/${encodeURIComponent(profile.nickname)}` : '/mypage',
                    { replace: true },
                  )
            }
          >
            <FiChevronLeft size={23} />
          </button>
          <h1>
            {locale === 'en'
              ? `${profile.nickname}’s ${t('taste.analysisTitle')}`
              : `${profile.nickname}님의 ${t('taste.analysisTitle')}`}
          </h1>
          <span />
        </header>
      ) : (
        <h2 className="pp-taste-summary-heading">
          {publicProfile ? publicTitle : t('my.musicTaste')}
        </h2>
      )}
      {loading && (
        <p className="pp-taste-status" role="status">
          {t('taste.organizing')}
        </p>
      )}
      {errors.length > 0 && (
        <div className="pp-taste-error" role="alert">
          <p>
            {publicProfile && !detail
              ? profileWords(locale)('profile.tasteLoadError')
              : t('taste.analysisError')}
          </p>
          <button type="button" onClick={() => errors.forEach((q) => q.refetch())}>
            {t('common.retry')}
          </button>
        </div>
      )}
      {!loading &&
        (publicProfile ? !errors.length : !taste.isError) &&
        taste.data &&
        (detail ? (
          taste.data.isReady ? (
            <Detail
              key={`${profile.id}-${taste.dataUpdatedAt}`}
              data={taste.data}
              profile={profile}
              t={t}
              locale={locale}
            />
          ) : (
            <Unlock data={taste.data} t={t} detail />
          )
        ) : (
          <Summary
            data={taste.data}
            features={features.data}
            t={t}
            locale={locale}
            publicProfile={publicProfile}
            profile={profile}
          />
        ))}
    </section>
  );
}
